import {useQueryClient} from '@tanstack/react-query'
import axios from 'axios'
import {useCallback, useRef} from 'react'
import {QUERY_KEYS} from '@/queryClient.ts'
import {
    HttpSendChatMessageOptions,
    httpSendChatMessage,
    InvalidSessionError
} from '@features/ai-chatbot/http/ai-chatbot.http'
import {useChatStore} from '@features/ai-chatbot/store/store.ts'
import {InteractionState, SSEEventData} from '@features/ai-chatbot/types.ts'

/**
 * Hook to handle SSE streaming from the chatbot API.
 * Parses Server-Sent Events and updates the chat store accordingly.
 */
export const useChatStream = (sessionId: string | undefined) => {
    const abortControllerRef = useRef<AbortController | null>(null)
    const queryClient = useQueryClient()

    const addMessage = useChatStore(state => state.addMessage)
    const setStreaming = useChatStore(state => state.setStreaming)
    const setCurrentStreamContent = useChatStore(state => state.setCurrentStreamContent)
    const appendStreamContent = useChatStore(state => state.appendStreamContent)
    const setInteractionState = useChatStore(state => state.setInteractionState)
    const setSuggestions = useChatStore(state => state.setSuggestions)
    const finalizeAssistantMessage = useChatStore(state => state.finalizeAssistantMessage)
    const setLastMessageAsError = useChatStore(state => state.setLastMessageAsError)

    const sendMessage = useCallback(
        async (request: Omit<HttpSendChatMessageOptions, 'sessionId'>) => {
            if (!sessionId) {
                throw new Error('Session not initialized')
            }

            abortControllerRef.current?.abort()
            abortControllerRef.current = new AbortController()

            addMessage({
                id: crypto.randomUUID(),
                role: 'user',
                content: request.text,
                timestamp: new Date()
            })
            addMessage({
                id: crypto.randomUUID(),
                role: 'assistant',
                content: '',
                timestamp: new Date()
            })

            setStreaming(true)
            setCurrentStreamContent('')
            setInteractionState({type: 'streaming'})
            setSuggestions([])

            const handleEvent = (event: SSEEventData) => {
                if (event.event === 'stream_start') {
                    setSuggestions(event.data.suggestions ?? [])
                    return
                }
                if (event.event === 'token') {
                    appendStreamContent(event.data.content)
                    if (event.data.suggestions?.length) {
                        setSuggestions(event.data.suggestions)
                    }
                    return
                }
                if (event.event === 'error') {
                    setLastMessageAsError()
                    setInteractionState({type: 'idle'})
                    return
                }
                if (event.event === 'conversation_ended') {
                    finalizeAssistantMessage()
                    setInteractionState({type: 'conversation_ended', reason: event.data.reason})
                    return
                }
                // Remaining events all finalize the message, update suggestions,
                // and transition to a new interaction state.
                finalizeAssistantMessage()
                setSuggestions(event.data.suggestions ?? [])
                setInteractionState(interactionStateFor(event))
            }

            try {
                const stream = await httpSendChatMessage({...request, sessionId}, abortControllerRef.current.signal)
                await parseSSEStream(stream, handleEvent)
            } catch (error) {
                if (axios.isCancel(error) || (error instanceof Error && error.name === 'AbortError')) {
                    return
                }
                if (error instanceof InvalidSessionError) {
                    queryClient.invalidateQueries({queryKey: [QUERY_KEYS.CHATBOT_SESSION]})
                    return
                }
                setLastMessageAsError()
                setInteractionState({type: 'idle'})
            } finally {
                setStreaming(false)
            }
        },
        [
            sessionId,
            queryClient,
            addMessage,
            setStreaming,
            setCurrentStreamContent,
            appendStreamContent,
            setInteractionState,
            setSuggestions,
            finalizeAssistantMessage,
            setLastMessageAsError
        ]
    )

    const abort = useCallback(() => {
        abortControllerRef.current?.abort()
        setStreaming(false)
        setInteractionState({type: 'idle'})
    }, [setStreaming, setInteractionState])

    return {sendMessage, abort}
}

const interactionStateFor = (event: SSEEventData): InteractionState => {
    switch (event.event) {
        case 'done':
            return {type: 'idle'}
        case 'param_required':
            return {
                type: 'param_required',
                param: event.data.param,
                inputType: event.data.inputType,
                operation: event.data.operation
            }
        case 'confirmation_required':
            return {type: 'confirmation_required', operation: event.data.operation}
        case 'anything_else_required':
            return {type: 'anything_else_required'}
        case 'rating_required':
            return {type: 'rating_required'}
        case 'comment_requested':
            return {type: 'comment_requested'}
        case 'comment_required':
            return {type: 'comment_required'}
        default:
            return {type: 'idle'}
    }
}

/**
 * Parses an SSE stream and invokes `onEvent` for each recognized event.
 * SSE format: `event: <name>\ndata: <payload>\n\n` — multiple `data:` lines
 * in a single block are joined with `\n` per the spec.
 */
const parseSSEStream = async (
    body: ReadableStream<Uint8Array>,
    onEvent: (event: SSEEventData) => void
): Promise<void> => {
    const reader = body
        .pipeThrough(new TextDecoderStream() as unknown as ReadableWritablePair<string, Uint8Array>)
        .getReader()
    let buffer = ''

    // eslint-disable-next-line no-constant-condition
    while (true) {
        const {done, value} = await reader.read()
        if (done) {
            if (buffer.trim()) {
                dispatchBlock(buffer, onEvent)
            }
            break
        }
        buffer += value
        const blocks = buffer.split('\n\n')
        buffer = blocks.pop() ?? ''
        for (const block of blocks) {
            if (block.trim()) {
                dispatchBlock(block, onEvent)
            }
        }
    }
}

const KNOWN_EVENTS: ReadonlySet<SSEEventData['event']> = new Set([
    'stream_start',
    'token',
    'done',
    'param_required',
    'confirmation_required',
    'anything_else_required',
    'rating_required',
    'comment_requested',
    'comment_required',
    'conversation_ended',
    'error'
])

const isKnownEvent = (name: string): name is SSEEventData['event'] => (KNOWN_EVENTS as ReadonlySet<string>).has(name)

const dispatchBlock = (block: string, onEvent: (event: SSEEventData) => void): void => {
    let eventName = ''
    const dataLines: string[] = []

    for (const line of block.split('\n')) {
        if (line.startsWith('event:')) {
            eventName = line.slice(6).trim()
        } else if (line.startsWith('data:')) {
            dataLines.push(line.slice(5).trimStart())
        }
    }

    if (!eventName || !dataLines.length) {
        return
    }
    if (!isKnownEvent(eventName)) {
        return
    }

    const dataStr = dataLines.join('\n')
    try {
        const data = JSON.parse(dataStr)
        onEvent({event: eventName, data} as SSEEventData)
    } catch (error) {
        console.error('Failed to parse SSE event', {eventName, dataStr, error})
    }
}
